import dayjs from "dayjs";
export interface MetaDataField {
  title: string;
  description: string;
  type: "string" | "number";
  format?: string;
  order: string;
}

export interface TabMetaData {
  [fieldKey: string]: MetaDataField;
}

// ─── Column format enum — driven by API "format" field ───────────────────────

export enum ColumnFormat {
  CURRENCY_GBP = "currency_gbp",
  CURRENCY_USD = "currency_usd",
  CURRENCY_EUR = "currency_eur", // dynamic EUR — resolved per row
  CURRENCY_MISSING = "currency_missing", // deal currency field absent or empty
  DATE = "date",
  DATETIME = "date-time",
  FLOAT = "float64",
  INT = "int64",
  STRING = "string",
  BOOLEAN = "boolean",
  UNKNOWN = "unknown",
}

const CURRENCY_COLUMN_FORMAT: Record<string, ColumnFormat> = {
  revshare_income: ColumnFormat.CURRENCY_USD,
  cpa_income: ColumnFormat.CURRENCY_USD,
  total_income: ColumnFormat.CURRENCY_USD,
  net_revenue: ColumnFormat.CURRENCY_GBP,
};

// Count columns the API sometimes sends as decimal-looking strings (e.g. "0.0")
// — always render these as whole numbers regardless of the declared metadata type.
const INT_COLUMN_KEYS = new Set(["registrations", "clicks"]);

// Columns whose currency follows cpa_deal_currency in the row
export const CPA_CURRENCY_COLUMNS = new Set(["cpa_income", "total_income"]);
// Columns whose currency follows revshare_deal_currency in the row
export const REVSHARE_CURRENCY_COLUMNS = new Set([
  "revshare_income",
  "net_revenue",
]);

const DATE_KEY_PATTERN = /(_date|_at|_time|_month)$/i;
const DOLLAR_KEY_PATTERN = /dollar/i;

// ─── Resolve format from metadata ────────────────────────────────────────────

export function resolveColumnFormat(
  field: MetaDataField | undefined,
  key?: string,
): ColumnFormat {
  if (!field) return ColumnFormat.UNKNOWN;

  // Static currency override — takes priority over API format field
  if (key && CURRENCY_COLUMN_FORMAT[key]) {
    return CURRENCY_COLUMN_FORMAT[key];
  }

  // Static whole-number override — takes priority over API format field
  if (key && INT_COLUMN_KEYS.has(key)) {
    return ColumnFormat.INT;
  }

  // Columns whose name contains "dollar" always render with $
  if (key && DOLLAR_KEY_PATTERN.test(key)) {
    return ColumnFormat.CURRENCY_USD;
  }

  const fmt = field?.format?.toLowerCase();

  switch (fmt) {
    case "currency":
      return ColumnFormat.CURRENCY_GBP;
    case "date":
      return ColumnFormat.DATE;
    case "date-time":
      return ColumnFormat.DATETIME;
    case "float64":
      return ColumnFormat.FLOAT;
    case "int64":
      return ColumnFormat.INT;
    default:
      if (field.type === "number") return ColumnFormat.FLOAT;
      // ⬇ Fallback: infer date from column name suffix
      if (key && DATE_KEY_PATTERN.test(key)) return ColumnFormat.DATE;
      return ColumnFormat.STRING;
  }
}

// ─── Per-row currency resolution ─────────────────────────────────────────────
// Maps a currency code string to a ColumnFormat. Returns null for unknown codes
// so the caller can continue to the next fallback.
function currencyCodeToFormat(code: string): ColumnFormat | null {
  switch (code.toUpperCase()) {
    case "EUR":
      return ColumnFormat.CURRENCY_EUR;
    case "USD":
      return ColumnFormat.CURRENCY_USD;
    case "GBP":
      return ColumnFormat.CURRENCY_GBP;
    default:
      return null;
  }
}

// For income columns, the display currency is driven by the deal currency stored
// in the row itself (cpa_deal_currency / revshare_deal_currency). Falls back to
// GBP when the specific deal currency field is absent or empty.
export function resolveEffectiveCurrencyFormat(
  colKey: string,
  baseFormat: ColumnFormat,
  row: Record<string, any>,
): ColumnFormat {
  let dealCurrencyCode: string | null = null;

  if (CPA_CURRENCY_COLUMNS.has(colKey)) {
    dealCurrencyCode = row?.cpa_deal_currency ?? null;
  } else if (REVSHARE_CURRENCY_COLUMNS.has(colKey)) {
    dealCurrencyCode = row?.revshare_deal_currency ?? null;
  } else {
    return baseFormat;
  }

  if (dealCurrencyCode && String(dealCurrencyCode).trim() !== "") {
    return (
      currencyCodeToFormat(String(dealCurrencyCode)) ??
      ColumnFormat.CURRENCY_GBP
    );
  }

  return ColumnFormat.CURRENCY_MISSING;
}

// ─── Build ordered column definitions from metadata ──────────────────────────

export interface ColumnDefinition {
  key: string;
  label: string;
  format: ColumnFormat;
  sortable: boolean;
}

// ─── Build headings from metadata ────────────────────────────────────────────
export function buildColumnDefinitionsFromMetaData(
  metaData: TabMetaData,
): ColumnDefinition[] {
  return Object.entries(metaData)
    .filter(
      ([, field]) => field.title?.trim() !== "" && field.order?.trim() !== "", // ← hide columns if the order is empty
    )
    .map(([key, field]) => ({
      key,
      label: getColumnLabel(key),
      format: resolveColumnFormat(field, key), // ← pass key
      sortable: true,
    }))
    .sort((a, b) => {
      const orderA = parseInt(metaData[a.key]?.order ?? "9999", 10);
      const orderB = parseInt(metaData[b.key]?.order ?? "9999", 10);
      return orderA - orderB;
    });
}

// ─── Fallback: build headings from a data record (kept for backwards compat) ──

// export function getColumnLabel(key: string): string {
//   return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
// }

const ACRONYMS = new Set([
  "id",
  "uid",
  "utc",
  "url",
  "cpa",
  "ftd",
  "qftd",
  "usd",
  "gbp",
]);

export function getColumnLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w+/g, (word) => {
    const lower = word.toLowerCase();
    return ACRONYMS.has(lower)
      ? word.toUpperCase()
      : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}

export function buildTableHeadingsFromRecord(
  record: Record<string, any>,
): Record<string, string> {
  return Object.fromEntries(
    Object.keys(record).map((key) => [key, getColumnLabel(key)]),
  );
}

// ─── Date formatters ──────────────────────────────────────────────────────────

export const formatLastRunDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  return new Intl.DateTimeFormat("en-US", options)
    .format(date)
    .replace(/^(\w{3})/, "$1.")
    .replace(/AM/i, "a.m.")
    .replace(/PM/i, "p.m.");
};

export const formatToUTC = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes} UTC`;
};

export const formatValueToDecimal = (
  value: any,
  decimals: number = 2,
): string => {
  const num = parseFloat(value);
  if (isNaN(num)) return value ?? "0";
  return num.toFixed(decimals);
};

// ─── Query IDs ────────────────────────────────────────────────────────────────

export const QUERY_IDS = {
  DAILY_USER_PERFORMANCE: "5723869612605440",
  DAILY_BRAND_PERFORMANCE: "5683428200546304",
  MONTHLY_BRAND_PERFORMANCE: "5643966779228160",
  OBJECTS_SECTION: "5668211466960896",
  BRAND_ACCOUNT_OVERVIEW_INFO: "5656631094280192",
  POPUPDATA: "5689069036109824",
};

export const REPORT_QUERY_ID_MAP: Record<string, string> = {
  daily_user: QUERY_IDS.DAILY_USER_PERFORMANCE,
  daily_brand: QUERY_IDS.DAILY_BRAND_PERFORMANCE,
  monthly_brand: QUERY_IDS.MONTHLY_BRAND_PERFORMANCE,
};

export const isValidParam = (val: string | null) =>
  !!val && val !== "null" && val !== "undefined";

//  Misc formatters
export const formatMetricName = (value: string) => {
  const acronyms: Record<string, string> = {
    cpa: "CPA",
    qftd: "QFTD",
    api: "API",
    uid: "UID",
    ftd: "FTD",
  };

  return value
    .split("_")
    .map(
      (word) =>
        acronyms[word.toLowerCase()] ??
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(" ");
};

export const formatDateTime = (dateString?: string | null): string => {
  if (!dateString) return "";

  return dayjs(dateString)
    .format("MMM. DD, YYYY, hh:mm a")
    .replace("am", "a.m.")
    .replace("pm", "p.m.");
};

// ─── Normalize api value (lowercase) ─────────────────────────────────────────────────────────
export const normalizeStep = (value?: string | null): string =>
  (value ?? "").trim().toLowerCase();
